import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Factory, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["name", "Plant name", "text", true], ["code", "Plant code", "text"],
  ["plant_type", "Plant type", "text"], ["address", "Address", "text"],
  ["region", "State / region", "text"], ["coordinates", "Coordinates", "text"],
  ["production_capacity", "Production capacity", "text"], ["annual_production", "Annual production", "text"],
  ["reference_period", "Reporting / reference period", "text"], ["electricity", "Electricity supply", "text"],
  ["fuels", "Fuels", "text"], ["water", "Water", "text"], ["waste", "Waste", "text"],
];

export function PlantDialog({ open, onOpenChange, plant, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => { setForm(plant || { country: "Nigeria" }); }, [plant, open]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try {
      if (plant?.id) await api.put(`/plants/${plant.id}`, form);
      else await api.post("/plants", form);
      toast.success("Plant saved");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{plant?.id ? "Edit plant" : "New plant"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {FIELDS.map(([k, label, , req]) => (
            <div key={k} className={k === "address" ? "col-span-2" : ""}>
              <Label className="text-xs">{label}{req && " *"}</Label>
              <Input data-testid={`plant-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1" />
            </div>
          ))}
          <div className="col-span-2">
            <Label className="text-xs">Major process information</Label>
            <Textarea data-testid="plant-process_info" value={form.process_info || ""} onChange={(e) => set("process_info", e.target.value)} className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="plant-save" onClick={save} disabled={!form.name}>Save plant</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Plants() {
  const [plants, setPlants] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/plants").then(({ data }) => setPlants(data));
  useEffect(() => { load(); }, []);

  return (
    <div>
      <PageHeader title="Plants" subtitle="Reusable plant master data — entered once, referenced across products and EPDs."
        action={<Button data-testid="add-plant-btn" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> Add plant</Button>} />
      {plants && plants.length === 0 ? (
        <Empty icon={Factory} title="No plants yet" hint="Add the manufacturing facility where your product is produced."
          action={<Button data-testid="empty-add-plant" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1.5" /> Add your first plant</Button>} />
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full text-sm epn-table">
            <thead className="bg-slate-50 border-b"><tr className="text-left">
              <th className="px-5 py-2.5">Plant</th><th className="px-5 py-2.5">Code</th>
              <th className="px-5 py-2.5">Type</th><th className="px-5 py-2.5">Region</th>
              <th className="px-5 py-2.5">Reference period</th><th className="px-5 py-2.5 text-right">Action</th>
            </tr></thead>
            <tbody>
              {(plants || []).map((p) => (
                <tr key={p.id} data-testid={`plant-row-${p.id}`} className="border-b last:border-0">
                  <td className="px-5 py-3 font-medium">{p.name}{p.is_demo && <span className="ml-2 text-[10px] rounded bg-amber-50 border border-amber-200 text-amber-700 px-1.5 py-0.5">DEMO</span>}</td>
                  <td className="px-5 py-3 font-mono text-xs">{p.code}</td>
                  <td className="px-5 py-3 text-slate-600">{p.plant_type}</td>
                  <td className="px-5 py-3 text-slate-600">{p.region}</td>
                  <td className="px-5 py-3 text-slate-600">{p.reference_period}</td>
                  <td className="px-5 py-3 text-right">
                    <Button size="sm" variant="ghost" data-testid={`edit-plant-${p.id}`} onClick={() => { setEditing(p); setOpen(true); }}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <PlantDialog open={open} onOpenChange={setOpen} plant={editing} onSaved={load} />
    </div>
  );
}
